import { useMemo } from 'react';
import { Anchor, Flex, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { DealEventTct, SessionInfo } from '@dealstack/shared/lib/backendClient';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { splitFullName } from '@dealstack/shared/lib/format/user';
import { AvatarInfoRow } from '@dealstack/frontend/shared/components/AvatarInfoRow/AvatarInfoRow';
import { SessionDetails } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/SessionDetails/SessionDetails';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
	useTranslationMipMessages,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatAuditSessionTitle } from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import {
	getDealEventChangeAction,
	getDealEventChanges,
	getDealEventDetailAction,
	getDealEventDetails,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogChanges.utils.ts';
import {
	dealEventRecordPath,
	formatDealEventHeading,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import { DealEventChangesTable } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventChangesTable.tsx';
import { DetailPanelLayout } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventDetailPanel.styles.tsx';

interface Props {
	event: DealEventTct;
	dealId: string;
}

const SessionPanel = ({ event, session }: { event: DealEventTct; session: SessionInfo }) => {
	const tCapManage = useTranslationCapManage();
	const { firstName, lastName } = splitFullName(session.fullName);

	return (
		<div className="deal-event-detail-session">
			<Flex direction="column" gap="0.5rem">
				<Text fz={14} c="gray.9">
					{formatAuditSessionTitle(event.action, tCapManage)}
				</Text>

				<AvatarInfoRow
					avatarSize="md"
					avatarTextSize="sm"
					firstName={firstName}
					lastName={lastName}
					text={session.fullName}
					subText={`(${session.email})`}
				/>
			</Flex>

			<SessionDetails
				mode="simple"
				bgColor="gray.0"
				fullName={session.fullName}
				email={session.email}
				startDateAndTime={session.createdAt}
				ipAddress={session.ipAddress}
				location={session.location}
				deviceInfo={session.deviceInfo}
				isLoading={false}
			/>
		</div>
	);
};

export const DealEventDetailPanel = ({ event, dealId }: Props) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();
	const tMipMessages = useTranslationMipMessages();
	const changes = useMemo(
		() => getDealEventChanges(event, tCapManage, tCommonUi),
		[event, tCapManage, tCommonUi]
	);
	const details = useMemo(() => getDealEventDetails(event, tCapManage), [event, tCapManage]);
	const recordPath = dealEventRecordPath(dealId, event);
	const { session } = event;

	// A create or a delete has no diff to show, so the panel falls back to what was recorded.
	const table = changes.length
		? {
				rows: changes,
				action: getDealEventChangeAction(event),
				testId: 'deal-event-changes-table',
			}
		: {
				rows: details,
				action: getDealEventDetailAction(event),
				testId: 'deal-event-details-table',
			};

	const subtitle = [
		formatStringDateToUI(event.occurredAt, DATE_FORMATS.DATE_TIME_ALT),
		changes.length > 0 &&
			tCapManage('deal_event_log.detail.state_changes', { count: changes.length }),
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<DetailPanelLayout>
			<div className="deal-event-detail-header">
				<Text fz={14} fw={600} c="dark.8">
					{formatDealEventHeading(event, tCapManage, tMipMessages, tCommonUi)}
				</Text>

				<Text fz={12} c="gray.9">
					{subtitle}
				</Text>
			</div>

			<Flex gap="0.75rem" align="flex-start" className="deal-event-detail-content">
				{table.rows.length > 0 && (
					<div className="deal-event-table">
						<DealEventChangesTable
							changes={table.rows}
							auditAction={table.action}
							dataTestId={table.testId}
						/>
					</div>
				)}

				{!!session && <SessionPanel event={event} session={session} />}
			</Flex>

			{!!recordPath && (
				<Anchor
					component={Link}
					to={recordPath}
					size="sm"
					mt="1rem"
					display="inline-block"
					className="deal-event-detail-link"
				>
					{tCommonUi('common_buttons.view_details')}
				</Anchor>
			)}
		</DetailPanelLayout>
	);
};
